import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CalendarClock, Star, X, Video } from "lucide-react";
import { PAYMENT_MAP } from "@/lib/payments";

export default function Bookings() {
  const [role, setRole] = useState("customer");
  const [items, setItems] = useState([]);
  const [reviewed, setReviewed] = useState({});
  const [reviewing, setReviewing] = useState(null);

  const load = () => api.get(`/bookings/mine?role=${role}`).then(r=>setItems(r.data));
  useEffect(()=>{ load(); }, [role]);

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
      <p className="overline mb-3">Bookings</p>
      <h1 className="h-display text-4xl mb-6">Your calendar of human time.</h1>

      <div className="flex gap-2 mb-8" data-testid="bookings-tabs">
        <button className={`pill-link ${role==="customer"?"active":""}`} onClick={()=>setRole("customer")} data-testid="bookings-tab-customer">As customer</button>
        <button className={`pill-link ${role==="hooman"?"active":""}`} onClick={()=>setRole("hooman")} data-testid="bookings-tab-hooman">As Hooman</button>
      </div>

      <div className="space-y-3" data-testid="bookings-list">
        {items.map((b) => {
          const past = new Date(b.date) < new Date();
          const isOnline = b.delivery_type === "online" && !!b.meeting_url;
          const scheduledMs = new Date(b.date).getTime();
          const now = Date.now();
          const minsUntil = Math.round((scheduledMs - now) / 60000);
          const joinWindowOpen = scheduledMs - now <= 15 * 60 * 1000 && now - scheduledMs <= 4 * 3600 * 1000;
          return (
          <div key={b.id} className="card p-6 flex flex-wrap items-center gap-4" data-testid={`booking-row-${b.id}`}>
            {role==="customer"
              ? (b.hooman_avatar && <img src={b.hooman_avatar} className="h-12 w-12 rounded-full object-cover" alt=""/>)
              : (b.customer?.avatar
                  ? <img src={b.customer.avatar} className="h-12 w-12 rounded-full object-cover" alt=""/>
                  : <div className="h-12 w-12 rounded-full bg-[#F2F2F0]"/>)}
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium flex items-center gap-2">
                {b.service_title}
                {isOnline && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0A0A0A] text-white inline-flex items-center gap-1" data-testid={`booking-online-badge-${b.id}`}>
                    <Video size={9}/> Online
                  </span>
                )}
              </p>
              <p className="text-sm text-[#4A4A4A]">
                {role==="customer" ? `with ${b.hooman_name}` : `for ${b.customer?.name||"a customer"}`}
              </p>
              <p className="text-xs text-[#8A8A8A] inline-flex items-center gap-1 mt-1"><CalendarClock size={12}/> {new Date(b.date).toLocaleString()}</p>
            </div>
            <div className="text-right space-y-2">
              <p className="font-heading text-xl">${b.price}</p>
              <span className="badge badge-verified">{b.status}</span>
              {b.payment_method && PAYMENT_MAP[b.payment_method] && (() => {
                const pm = PAYMENT_MAP[b.payment_method]; const Icon = pm.Icon;
                return <p className="text-xs text-[#4A4A4A] inline-flex items-center gap-1 justify-end" data-testid={`booking-pm-${b.id}`}><Icon size={12}/> {pm.label}</p>;
              })()}
              {isOnline && (
                joinWindowOpen ? (
                  <Link to={`/meet/${b.id}`} className="block btn-primary !py-2 !px-4 text-sm" data-testid={`booking-join-${b.id}`}>
                    <Video size={14}/> Join video call
                  </Link>
                ) : (
                  <Link to={`/meet/${b.id}`} className="block btn-secondary !py-2 !px-4 text-sm" data-testid={`booking-room-${b.id}`}>
                    <Video size={14}/> {minsUntil > 0 ? `Open room · in ${minsUntil}m` : "Open room"}
                  </Link>
                )
              )}
              {role==="customer" && past && !reviewed[b.id] && (
                <button onClick={()=>setReviewing(b)} className="block btn-secondary !py-2 !px-4 text-sm" data-testid={`leave-review-${b.id}`}>
                  <Star size={14}/> Leave review
                </button>
              )}
              {reviewed[b.id] && <p className="text-xs text-[#0A0A0A]">★ Review posted</p>}
            </div>
          </div>
        );})}
        {items.length === 0 && <p className="text-[#8A8A8A] text-center mt-12">No bookings yet.</p>}
      </div>

      {reviewing && (
        <ReviewModal
          booking={reviewing}
          onClose={()=>setReviewing(null)}
          onDone={(b)=>{ setReviewed({...reviewed, [b.id]: true}); setReviewing(null); }}
        />
      )}
    </div>
  );
}

function ReviewModal({ booking, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post("/reviews", {
        hooman_id: booking.hooman_id, rating, text, booking_id: booking.id,
      });
      onDone(booking);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="review-modal">
      <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="bg-white rounded-3xl p-8 max-w-md w-full">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline mb-1">Leave a review</p>
            <h3 className="font-heading text-2xl">{booking.hooman_name}</h3>
            <p className="text-sm text-[#4A4A4A]">{booking.service_title}</p>
          </div>
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="review-modal-close"><X size={16}/></button>
        </div>
        <div className="flex gap-1 mb-4" data-testid="modal-stars">
          {[1,2,3,4,5].map(n=>(
            <button type="button" key={n} onClick={()=>setRating(n)} data-testid={`modal-star-${n}`}>
              <Star size={28} className={n<=rating?"fill-[#0057FF] stroke-[#0057FF]":"stroke-[#8A8A8A]"}/>
            </button>
          ))}
        </div>
        <textarea required rows={4} value={text} onChange={(e)=>setText(e.target.value)}
          className="input" placeholder="Share what made it special" data-testid="modal-review-text"/>
        {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}
        <button className="btn-primary w-full mt-5" disabled={saving} data-testid="modal-review-submit">{saving?"Posting…":"Post review"}</button>
      </form>
    </div>
  );
}
